import { ChangeDetectorRef, Component, ElementRef, Inject, KeyValueDiffers, OnChanges, OnInit, ViewChild, forwardRef } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';
import moment from 'moment-timezone';

import { download } from '../../../../Misc/helpers/download/download.helper';
import { ActionKeys } from '../../../../Forms/enums/keys.enum';
import { BlockComponent } from '../../../../PageBuilder/components/Block/block.component';
import { ChartDisplayElementModel } from '../../../../Reports/models/chart-display-element.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SeriesModel } from '../../../../Charts/models/series.model';
import { TopMentionsChartComponent } from '../TopMentionsChart/top-mentions-chart.component';
import { ReportsChartOptionsService } from '../../../services/reports-chart-options.service';
import { AbstractPageBuilderService } from '../../../../PageBuilder/services/abstract-page-builder.service';
import { ClickOutsideService } from '../../../../PageBuilder/services/click-outside.service';
import { CurrencyConversionService } from '../../../../../Core/Users/services/currency-conversion.service';
import { mentionSearchResultApiMock } from '../../../../../api/mentions/mocks/search-result.mock';
import { Mention } from '../../../../Mentions/models/mention';
import { ChartExportEventModel } from '../../../../Charts/models/chart-export-event.model';
import { MentionMetadataTypesEnum } from '../../../../../Monitor/Shared/enums/mention-metadata-types.enum';
import { MentionContentCustomizationEnum } from '../../../../Mentions/enums/mention-content-customization.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { printHelper } from '../../../../Misc/helpers/print/print.helper';
import { ReportsPageBuilderService } from '../../../services/reports-page-builder.service';
import { ChartBlockComponent } from '../../ChartBlock/chart-block.component';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { ProminenceLockerService } from '../../../../Misc/services/prominence-locker.service';
import { MonitorConfigService } from '../../../../MonitorShared/services/monitor-config.service';
import { ReportsSeriesPreviewService } from '../../../../Reports/services/report-series-preview.service';
import { ReportsChartDetailService } from '../../../../Reports/services/reports-chart-detail.service';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { SocialChartDetailsDialogService } from '../../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { DateQueryTypeEnum } from '../../../../../api/mentions/enum/date-query-type.enum';
import { DataConnectionService } from '../../../../../Core/Users/services/data-connection.service';
import { chartColors } from '../../../../Charts/enums/chart-colors.enum';
import { ReportsChartSeriesConverterService } from '../../../services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../../Common/Reports/services/reports-series-preview-new.service';


@Component({
    selector: 'agpr-top-mentions-chart-block',
    templateUrl: './top-mentions-chart-block.component.html',
    styleUrls: ['./top-mentions-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => TopMentionsChartBlockComponent)
        }
    ],
    standalone: false
})
export class TopMentionsChartBlockComponent extends ChartBlockComponent implements OnInit, OnChanges {

    protected exportClass: string = 'agpr-chartBlock--exporting';

    displayElement: ChartDisplayElementModel;
    series: SeriesModel[];
    noHeader: boolean = false;
    exporting: boolean = false;
    isExporting: boolean = false;
    public sourceRouterLink: string = '';
    public sourceRouterLinkQueryParams: any = {};

    readonly topMentionsSortFields = SearchCriteriaSortFieldEnum;
    readonly messageSeverities = messageSeverityEnum;

    @ViewChild(TopMentionsChartComponent) topMentionsChart: TopMentionsChartComponent;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public cdr: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public prominenceLockerService: ProminenceLockerService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public el: ElementRef,
        public currencyConversionService: CurrencyConversionService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            cdr,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            el,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initSourceRouterLink();
        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        this.displayElement.topMentionsMetadata.dateFormat = this.reportsChartOptionsService.getDateFormat();
        this.displayElement.topMentionsMetadata.currencyRate =
            this.currencyConversionService.getCurrencyRate(this.displayElement.topMentionsMetadata.preferredCurrency);
    }

    // When fields change, this will listen to the changes
    ngOnChanges(): void {
        super.ngOnChanges();
        this.updateChart();
        if (this.topMentionsChart) {
            this.topMentionsChart.cdr.detectChanges();
        }
    }

    updateChart() {
        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (this.preview) {
            const mentions = mentionSearchResultApiMock.mentions.map((model) => new Mention(model));
            const copy = this.block.displayElement.clone();
            (copy as ChartDisplayElementModel).series[0].mentionProvider.next(mentions);
            this.displayElement = copy as ChartDisplayElementModel;
        }
        this.series = this.displayElement.series;
        this.setLabels();
        if ((this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent) {
            (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
        }
    }

    protected setLabels(): void  {
        if (this.displayElement?.series?.[0]?.dateRange
            // For shared report, we already set its relative and absolute date rage when mapping api blocks
            // For date range 'ALL' scenario, we cannot do localization in model, so we keep it here
            && (!this.displayElement.series[0].isSharedReport || this.displayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series);
        }
    }

    dragKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT
            || event.key === ActionKeys.UP || event.key === ActionKeys.DOWN) {
            this.outputEvent.emit(event);
        }
    }

    public async chartExport(event: ChartExportEventModel) {
        this.isExporting = true;
        const wrapper: HTMLDivElement = this.el.nativeElement.children[0];
        const container = wrapper.querySelector('.agpr-block.agpr-chartBlock') as HTMLDivElement;

        if (event.eventKey === 'png' || event.eventKey === 'jpg' || event.eventKey === 'svg' || event.eventKey === 'print') {
            this.exporting = true;
            // this removes the :hover rule that causes the border of the chart to be blue
            container.classList.remove('agpr-block');
            container.classList.remove('agpr-chartBlock');
        }

        let url: string;
        let blob: Blob;

        requestAnimationFrame(async () => {
            switch (event.eventKey) {
                case 'png':
                    url = await htmlToImage.toPng(container);
                    break;
                case 'jpg':
                    url = await htmlToImage.toJpeg(container);
                    break;
                case 'svg':
                    url = await htmlToImage.toSvg(container);
                    break;
                case 'csv':
                    const csvData = {
                        fields: [],
                        data: []
                    };

                    this.topMentionsChart.mentions.forEach((mention, index) => {
                        const csvMention = [];
                        csvMention.push(mention.headline);

                        if (index === 0) {
                            csvData.fields.push(this.localizationService.transform('Reports.topMentions.metadata.headline'));
                        }

                        if (this.displayElement.topMentionsMetadata.mentionContent !== MentionContentCustomizationEnum.headlineOnly) {
                            csvMention.push(mention.body);

                            if (index === 0) {
                                csvData.fields.push(this.localizationService.transform('Reports.topMentions.metadata.body'));
                            }
                        }

                        if (this.displayElement.topMentionsMetadata.showInsights) {
                            csvMention.push(mention.insights?.length ? mention.insights[0].content : '');

                            if (index === 0) {
                                csvData.fields.push(
                                    !!this.displayElement.topMentionsMetadata.customInsightsName ?
                                        this.displayElement.topMentionsMetadata.customInsightsName :
                                        this.localizationService.transform('Reports.topMentions.metadata.insights')
                                );
                            }
                        }

                        (Object.keys(MentionMetadataTypesEnum) as MentionMetadataTypesEnum[]).forEach((type) => {
                            if (this.displayElement.topMentionsMetadata.metadataTypes.includes(type)) {
                                if (index === 0) {
                                    csvData.fields.push(this.localizationService.transform('Reports.topMentions.metadata.' + type));
                                }

                                switch (type) {
                                    case MentionMetadataTypesEnum.mediaType:
                                        csvMention.push(this.localizationService.transform(mention.mediaType.name));
                                        break;
                                    case MentionMetadataTypesEnum.outlet:
                                        csvMention.push(mention.publication.name);
                                        break;
                                    case MentionMetadataTypesEnum.author:
                                        csvMention.push(mention.author.name);
                                        break;
                                    case MentionMetadataTypesEnum.ave:
                                        csvMention.push(mention.adValueUsd / this.displayElement.topMentionsMetadata.currencyRate);
                                        break;
                                    case MentionMetadataTypesEnum.publishedDate:
                                        csvMention.push(mention.datePublished);
                                        break;
                                    case MentionMetadataTypesEnum.duration:
                                        csvMention.push(moment(mention.duration * 1000).format('mm:ss'));
                                        break;
                                    default:
                                        csvMention.push(mention[type]);
                                        break;
                                }
                            }
                        });

                        if (mention.isFinancialTimes) {
                            if (index === 0) {
                                csvData.fields.push('Attribution');
                            }
                            csvMention.push(mention.attribution);
                        }
                        csvData.data.push(csvMention);
                    });

                    const csvString: string = papaparse.unparse(csvData, {delimiter: ','});

                    blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});

                    break;
                case 'json':
                    const mentions = [];

                    this.topMentionsChart.mentions.forEach((mention, index) => {
                        const jsonMention = {};
                        jsonMention['headline'] = mention.headline;

                        if (this.displayElement.topMentionsMetadata.mentionContent !== MentionContentCustomizationEnum.headlineOnly) {
                            jsonMention['body'] = mention.body;
                        }

                        if (this.displayElement.topMentionsMetadata.showInsights) {
                            const label = this.displayElement.topMentionsMetadata.customInsightsName || 'insights';
                            jsonMention[label] = mention.insights?.length ? mention.insights[0].content : '';
                        }

                        (Object.keys(MentionMetadataTypesEnum) as MentionMetadataTypesEnum[]).forEach((type) => {
                            if (this.displayElement.topMentionsMetadata.metadataTypes.includes(type)) {
                                switch (type) {
                                    case MentionMetadataTypesEnum.mediaType:
                                        jsonMention[type] = this.localizationService.transform(mention.mediaType.name);
                                        break;
                                    case MentionMetadataTypesEnum.outlet:
                                        jsonMention[type] = mention.publication.name;
                                        break;
                                    case MentionMetadataTypesEnum.author:
                                        jsonMention[type] = mention.author.name;
                                        break;
                                    case MentionMetadataTypesEnum.ave:
                                        jsonMention[type] = mention.adValueUsd / this.displayElement.topMentionsMetadata.currencyRate;
                                        break;
                                    case MentionMetadataTypesEnum.publishedDate:
                                        jsonMention[type] = mention.datePublished;
                                        break;
                                    case MentionMetadataTypesEnum.duration:
                                        jsonMention[type] = moment(mention.duration * 1000).format('mm:ss');
                                        break;
                                    default:
                                        jsonMention[type] = mention[type];
                                        break;
                                }
                            }
                        });

                        if (mention.isFinancialTimes) {
                            jsonMention['attribution'] = mention.attribution;
                        }

                        mentions.push(jsonMention);
                    });

                    const jsonData = {
                        'mentions': mentions
                    };

                    blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                    break;
                case 'print':
                    const printElement = this.el.nativeElement.children[0];
                    const printData = await htmlToImage.toSvg(printElement);
                    printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                    break;
            }

            if (url) {
                download.url(url, `${this.displayElement.title}.${event.eventKey}`);
            }

            if (blob) {
                download.blob(blob, `${this.displayElement.title}.${event.eventKey}`);
            }

            if (event.eventKey === 'png' || event.eventKey === 'jpg' || event.eventKey === 'svg' || event.eventKey === 'print') {
                container.classList.add('agpr-block');
                container.classList.add('agpr-chartBlock');
                this.exporting = false;
            }
            this.isExporting = false;
        });
    }

    /**
     * Gets the export image for the full report pdf export
     * @NOTE don't remove the width/height params.
     */
    public async getFullExportImage(
        width: number,
        height: number,
        transparent: boolean = false
    ): Promise<string> {
        const el: HTMLDivElement = this.el.nativeElement.children[0];
        const classes: DOMTokenList = this.el.nativeElement.children[0].classList;

        const metaDataHeight = el.getElementsByClassName('agpr-chartBlockMetadata')?.[0]?.clientHeight || 0;
        const mention = el.getElementsByClassName('agpr-topMentionsChart-container')?.[0];
        const newHeight = mention.getElementsByClassName('p-scrollpanel-content')?.[0].scrollHeight || 0;

        const metaDataWdith = el.getElementsByClassName('agpr-chartBlockMetadata')?.[0]?.clientWidth || 0;

        el.style.border = 'none';

        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = (metaDataWdith + 40) + 'px';
        el.style.minWidth = (metaDataWdith + 40) + 'px';
        el.style.height = (metaDataHeight + newHeight) + 'px';
        el.style.minHeight = (metaDataHeight + newHeight) + 'px';
        el.style.flex = 'unset';

        // There's no easy way to extend the length of the chart image to show all the data.
        // Slice the data to 5 before gererating the image because it gets cut off when data length is more than 5.
        // when image is generated, put the original data back otherwise it will only show 5 .
        const tempData = this.topMentionsChart.mentions;
        if (this.topMentionsChart.mentions) {
            this.topMentionsChart.mentions = this.topMentionsChart.mentions.slice(0, 5);
        }

        let png;
        try {
            // it will fail to create the pdf.. but at least it won't hang
            png = await htmlToImage.toPng(this.el.nativeElement.children[0]);
        } catch (e) {}

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;
        this.topMentionsChart.mentions = tempData;
        classes.remove(this.exportClass);
        return png;
    }

    /**
     * Constructs the url to the topic or folder currently being used as a source for this chart.
     * Tags & Tag Groups are also handled, by constructing a url to all saved coverage with appropriate filters
     * applied via queryParams
     */
    public initSourceRouterLink(): void {
        const displayElement = this.block.displayElement as ChartDisplayElementModel;

        if (displayElement?.series?.[0]) {
            const urlParts = displayElement.series[0].getMentionSeriesUrl().split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    formatDateRangeLabel(format: string) {
        this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format);
        this.changeDetectorRef.detectChanges();
    }
}
